import Udesk from 'Udesk';
import UdeskLocales from 'UdeskLocales';
import React, { useEffect, useState } from 'react';
import { useRequest } from 'ahooks';
import { Button, Empty, Icon, message, Space, Spin } from 'udesk-ui';
import { fetchEventSource } from 'udesk-llm-fe/dist/utils/fetch/fetchEventSource';
import { useApiPath } from 'src/util/hook/request';
import { inspect, isDefined, isNotEmpty } from 'src/util/core';
import { useDebounce } from 'src/util/hook';
import { getQuestionExtractionsByCallId } from 'src/api/questionExtractions/{callId}';

export default React.memo<{
    callId: number;
    thirdParty: boolean | undefined;
    onLocate?: (d: any, datasource?: any[]) => void;
    height?: number;
    defaultQuestionId?: string;
    isCaseLibInAppeal?: boolean;
    sdkOptions?: any;
}>((props) => {
    const { height, defaultQuestionId, onLocate, isCaseLibInAppeal, sdkOptions } = props;

    const [list, setList] = useState<any[] | undefined>([]);
    const [loading, setLoading] = useState(false);
    const getApiPath = useApiPath();
    const {
        loading: initLoading,
        data,
        run: getQuestionExtractions,
    } = useRequest(
        () =>
            isCaseLibInAppeal
                ? new Promise((resolve, reject) => {
                      Udesk.business.sdkAjax
                          .get(`open_api_v1/questionExtractions/${props.callId}`, sdkOptions, {
                              props,
                          })
                          .then(
                              (res) => {
                                  resolve(res);
                              },
                              (reason) => {
                                  message.error(reason.errorMsg);
                                  reject(reason);
                              }
                          );
                  })
                : getQuestionExtractionsByCallId({
                      segments: { callId: props.callId },
                  }),
        {
            ready: !!props.callId,
            refreshDeps: [props.callId],
        }
    );

    const getLLMText = useDebounce(() => {
        const text: string[] = [];
        setLoading(true);
        if (props.callId) {
            fetchEventSource(
                getApiPath(
                    `/questionExtractions/model/${props.callId}?clientId=${Math.ceil(
                        Math.random() * 1000000
                    )}`
                ),
                {
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    onmessage(resp) {
                        text.push(resp.data);
                    },
                    onclose() {
                        getQuestionExtractions();
                        setLoading(false);
                    },
                    onerror(err) {
                        setLoading(false);
                        throw err;
                    },
                }
            );
        }
    }, [getApiPath, props.callId]);

    const [hasShowDefault, setHasShowDefault] = useState(false);

    useEffect(() => {
        try {
            const _list = (data as any)?.data?.modelAnswer
                ? JSON.parse((data as any).data.modelAnswer)
                : undefined;

            if (defaultQuestionId && !hasShowDefault) {
                const item = _list?.find((item) => {
                    return `${item.id}` === `${defaultQuestionId}`;
                });
                if (item) {
                    onLocate?.(item, _list);
                    setHasShowDefault(true);
                }
            }

            setList(_list);
        } catch (e) {
            setList(undefined);
        }
    }, [data, hasShowDefault, onLocate, defaultQuestionId]);

    return (
        <div
            style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                overflow: 'auto',
                height,
            }}
        >
            {inspect(
                initLoading,
                () => (
                    <Spin spinning={initLoading}>
                        <div className={'problem-extraction-markdown'}></div>
                    </Spin>
                ),
                () => (
                    <>
                        {isNotEmpty(
                            list,
                            () => (
                                <div
                                    style={{
                                        flex: 1,
                                        padding: 16,
                                        overflow: 'auto',
                                    }}
                                >
                                    <Spin spinning={loading}>
                                        <div className={'problem-extraction-markdown'}>
                                            {list?.map((item) => (
                                                <div
                                                    style={{
                                                        marginBottom: 16,
                                                        color: '#555',
                                                        fontSize: 14,
                                                    }}
                                                >
                                                    <Space>
                                                        {props.onLocate &&
                                                        item.highlightGroups &&
                                                        item.highlightGroups.length ? (
                                                            <Button
                                                                type="text"
                                                                size="small"
                                                                icon={<Icon type="daohang" />}
                                                                onClick={() =>
                                                                    onLocate?.(item, list)
                                                                }
                                                            ></Button>
                                                        ) : null}

                                                        {item.category ? (
                                                            <span
                                                                style={{
                                                                    padding: '0 5px',
                                                                    borderRadius: '8px',
                                                                    background: '#1b6dff',
                                                                    color: 'white',
                                                                }}
                                                            >
                                                                {item.category === 1
                                                                    ? /* 个性问题 */ UdeskLocales[
                                                                          'current'
                                                                      ].components
                                                                          .qualityCheckScoreTalk
                                                                          .components
                                                                          .problemExtraction
                                                                          .personalityIssues
                                                                    : item.category === 2
                                                                    ? /* 通用问题 */ UdeskLocales[
                                                                          'current'
                                                                      ].components
                                                                          .qualityCheckScoreTalk
                                                                          .components
                                                                          .problemExtraction
                                                                          .generalQuestions
                                                                    : ''}
                                                            </span>
                                                        ) : null}

                                                        {item.questionType ? (
                                                            <span
                                                                style={{
                                                                    padding: '0 5px',
                                                                    borderRadius: '8px',
                                                                    background: '#1b6dff',
                                                                    color: 'white',
                                                                }}
                                                            >
                                                                {item.questionType}
                                                            </span>
                                                        ) : null}

                                                        <span
                                                            style={{
                                                                color:
                                                                    item.isAnswer === 1
                                                                        ? 'green'
                                                                        : 'red',
                                                            }}
                                                        >
                                                            {item.isAnswer === 1
                                                                ? /* 客服已解答 */ UdeskLocales[
                                                                      'current'
                                                                  ].components.qualityCheckScoreTalk
                                                                      .components.problemExtraction
                                                                      .customerServiceHasAnswered
                                                                : /* 客服未解答 */ UdeskLocales[
                                                                      'current'
                                                                  ].components.qualityCheckScoreTalk
                                                                      .components.problemExtraction
                                                                      .customerServiceDidNotAnswer}
                                                        </span>

                                                        <span
                                                            style={{
                                                                color:
                                                                    item.isSolve === 1
                                                                        ? 'green'
                                                                        : 'red',
                                                            }}
                                                        >
                                                            {item.isSolve === 1
                                                                ? /* 问题已解决 */ UdeskLocales[
                                                                      'current'
                                                                  ].components.qualityCheckScoreTalk
                                                                      .components.problemExtraction
                                                                      .theProblemHasBeenResolved
                                                                : /* 问题未解决 */ UdeskLocales[
                                                                      'current'
                                                                  ].components.qualityCheckScoreTalk
                                                                      .components.problemExtraction
                                                                      .problemUnresolved}
                                                        </span>

                                                        {item.emotion ? (
                                                            <span
                                                                style={{
                                                                    color:
                                                                        item.emotion === 1
                                                                            ? 'green'
                                                                            : item.emotion === 2
                                                                            ? 'red'
                                                                            : '',
                                                                }}
                                                            >
                                                                {item.emotion === 1
                                                                    ? /* 正面情绪 */ UdeskLocales[
                                                                          'current'
                                                                      ].components
                                                                          .qualityCheckScoreTalk
                                                                          .components
                                                                          .problemExtraction
                                                                          .positiveEmotions
                                                                    : item.emotion === 2
                                                                    ? /* 负面情绪 */ UdeskLocales[
                                                                          'current'
                                                                      ].components
                                                                          .qualityCheckScoreTalk
                                                                          .components
                                                                          .problemExtraction
                                                                          .negativeEmotion
                                                                    : item.emotion === 3
                                                                    ? /* 中性情绪 */ UdeskLocales[
                                                                          'current'
                                                                      ].components
                                                                          .qualityCheckScoreTalk
                                                                          .components
                                                                          .problemExtraction
                                                                          .neutralEmotion
                                                                    : ''}
                                                            </span>
                                                        ) : null}
                                                    </Space>

                                                    <div
                                                        style={{
                                                            marginLeft: 5,
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {
                                                                /* 客户问题： */ UdeskLocales[
                                                                    'current'
                                                                ].components.qualityCheckScoreTalk
                                                                    .components.problemExtraction
                                                                    .customerIssue
                                                            }
                                                        </div>
                                                        <div>{item.questionContent || '--'}</div>
                                                    </div>
                                                    <div
                                                        style={{
                                                            marginLeft: 5,
                                                            display: 'flex',
                                                        }}
                                                    >
                                                        <div
                                                            style={{
                                                                whiteSpace: 'nowrap',
                                                            }}
                                                        >
                                                            {
                                                                /* 客服解答： */ UdeskLocales[
                                                                    'current'
                                                                ].components.qualityCheckScoreTalk
                                                                    .components.problemExtraction
                                                                    .customerServiceAnswer
                                                            }
                                                        </div>
                                                        <div style={{ wordBreak: 'break-all' }}>
                                                            {item.kfAnswer || '--'}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </Spin>
                                </div>
                            ),
                            () => (
                                <div>
                                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                                </div>
                            )
                        )}

                        {!props.thirdParty && !isCaseLibInAppeal && (
                            <div
                                style={{
                                    padding: 16,
                                    textAlign: 'center',
                                }}
                            >
                                <Button
                                    disabled={loading}
                                    onClick={() => getLLMText()}
                                    type="primary"
                                >
                                    {isDefined(
                                        list,
                                        /* 重新抽取问题 */ UdeskLocales['current'].components
                                            .qualityCheckScoreTalk.components.problemExtraction
                                            .reExtractQuestions,
                                        /* 点击抽取问题 */ UdeskLocales['current'].components
                                            .qualityCheckScoreTalk.components.problemExtraction
                                            .clickToExtractQuestions
                                    )}
                                </Button>
                            </div>
                        )}
                    </>
                )
            )}
        </div>
    );
});
